import React from "react";
import moment from "moment";
import constants from "constants/constants";
import {
  FAILED_STATUS,
  QR_FILTER_KEYS,
  QR_STATUS_TEXT,
  QR_CONVERSION_TEXT,
  QR_TYPE_TEXT,
  QR_PAYMENT_STATUS_CODES,
  STATUS_BADGE_COLORS,
  QR_FILTER_LABELS,
  QR_CHIP_DATE_FORMAT,
  TABLE_THEME_COLORS,
  BADGE_VARIANTS,
  BADGE_STYLES,
  QR_TYPE_BADGE_STYLES,
} from "./qrPayments.constants";

export const QrStatusBadge = ({ variant, color, label }) => {
  const badgeStyle =
    variant && BADGE_STYLES[variant]
      ? BADGE_STYLES[variant]
      : { backgroundColor: color || STATUS_BADGE_COLORS.PENDING_BG };

  return (
    <span
      className="tw-inline-flex tw-items-center tw-text-[14px] tw-leading-[22px] tw-rounded-[5px] tw-px-2 tw-font-normal tw-text-white"
      style={badgeStyle}
    >
      {label}
    </span>
  );
};

export const QrTypeBadge = ({ type }) => {
  const badgeStyle = QR_TYPE_BADGE_STYLES[type];
  if (!badgeStyle) return "N/A";
  return (
    <span
      className="tw-px-2.5 tw-py-1 tw-rounded-[5px] tw-text-[14px] tw-inline-block"
      style={{
        backgroundColor: badgeStyle.bg,
        color: badgeStyle.text,
      }}
    >
      {badgeStyle.label}
    </span>
  );
};

export const getTableConfig = [
  {
    field: "qr_order_uuid",
    headerName: QR_FILTER_LABELS.TRANSACTION_ID,
    emptyField: "N/A",
    sortable: false,
    width: "220px",
    fixed: "left",
  },
  {
    field: "link_title",
    headerName: QR_FILTER_LABELS.LINK_NAME,
    emptyField: "N/A",
    sortable: false,
    width: "200px",
  },
  {
    field: "amount",
    headerName: "Amount",
    emptyField: "N/A",
    sortable: false,
    width: "140px",
    valueFormatter: (record) => {
      const amountVal = record.amount;
      if (amountVal === undefined || amountVal === null) return "N/A";
      const formatted = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: record.currency || "INR",
        maximumFractionDigits: 2,
      }).format(amountVal);
      return (
        <span
          style={{ fontWeight: 600, color: TABLE_THEME_COLORS.AMOUNT_TEXT }}
        >
          {formatted}
        </span>
      );
    },
  },
  {
    field: "payment_details",
    headerName: "Payment Details",
    emptyField: "N/A",
    sortable: false,
    width: "220px",
    valueFormatter: (record) => {
      const payerIdentifier = record?.payer_identifier;
      const paymentInstrument = record?.payment_instrument;

      if (!payerIdentifier && !paymentInstrument) {
        return "N/A";
      }

      return (
        <div className="tw-flex tw-flex-col tw-w-full tw-max-w-full tw-min-w-0 tw-text-sm tw-leading-6">
          <div>
            <strong>Payer Identifier:</strong> {payerIdentifier || "N/A"}
          </div>
          <div>
            <strong>Payment Instrument:</strong> {paymentInstrument || "N/A"}
          </div>
        </div>
      );
    },
  },
  {
    field: "paid_at",
    headerName: "Transaction Date",
    emptyField: "N/A",
    sortable: false,
    width: "190px",
    valueFormatter: (record) => {
      const dateVal = record.paid_at;
      if (!dateVal) return "N/A";
      return moment(dateVal).format(
        `${constants.displayDateYearFormat}, hh:mm A`
      );
    },
  },
  {
    field: "qr_type",
    headerName: QR_FILTER_LABELS.QR_TYPE,
    emptyField: "N/A",
    sortable: false,
    width: "180px",
    valueFormatter: (record) => <QrTypeBadge type={Number(record.qr_type)} />,
  },
  {
    field: "customer_full_name",
    headerName: QR_FILTER_LABELS.CUSTOMER_NAME,
    emptyField: "N/A",
    sortable: false,
    width: "180px",
  },
  {
    field: "status",
    headerName: QR_FILTER_LABELS.PAYMENT_STATUS,
    emptyField: "N/A",
    sortable: false,
    width: "160px",
    valueFormatter: (record) => {
      const status = Number(record.status);
      if (status === QR_PAYMENT_STATUS_CODES.PENDING) {
        return (
          <QrStatusBadge
            variant={BADGE_VARIANTS.PENDING}
            label={QR_STATUS_TEXT.PENDING}
          />
        );
      }
      if (status === QR_PAYMENT_STATUS_CODES.SUCCESS) {
        return (
          <QrStatusBadge
            variant={BADGE_VARIANTS.SUCCESS}
            label={QR_STATUS_TEXT.SUCCESS}
          />
        );
      }
      if (status === QR_PAYMENT_STATUS_CODES.FAILED) {
        return (
          <QrStatusBadge
            variant={BADGE_VARIANTS.FAILED}
            label={QR_STATUS_TEXT.FAILED}
          />
        );
      }
      return "N/A";
    },
  },
  {
    field: "is_converted",
    headerName: QR_FILTER_LABELS.CONVERSION_STATUS,
    emptyField: "N/A",
    sortable: false,
    width: "160px",
    valueFormatter: (record) => {
      if (Number(record?.status) === FAILED_STATUS) {
        return "N/A";
      }
      const isConverted = record?.is_converted;
      if (isConverted) {
        return (
          <QrStatusBadge
            variant={BADGE_VARIANTS.SUCCESS}
            label={QR_CONVERSION_TEXT.CONVERTED}
          />
        );
      }
      return (
        <QrStatusBadge
          variant={BADGE_VARIANTS.PENDING}
          label={QR_CONVERSION_TEXT.PENDING}
        />
      );
    },
  },
];

export const filterConfig = () => [
  {
    source: "customer_full_name",
    placeholder: QR_FILTER_LABELS.CUSTOMER_NAME,
    type: "input",
    disableContains: true,
  },
  {
    source: "link_title",
    placeholder: QR_FILTER_LABELS.LINK_NAME,
    type: "input",
    disableContains: true,
  },
  {
    source: "qr_order_uuid",
    placeholder: QR_FILTER_LABELS.TRANSACTION_ID,
    type: "input",
    disableContains: true,
  },
  {
    source: "qr_type",
    placeholder: QR_FILTER_LABELS.QR_TYPE,
    options: [
      { value: 1, label: QR_TYPE_TEXT.DIRECT_UPI },
      { value: 2, label: QR_TYPE_TEXT.STANDARD_GATEWAY },
    ],
    type: "dropdown",
  },
  {
    source: "status",
    placeholder: QR_FILTER_LABELS.PAYMENT_STATUS,
    options: [
      { value: QR_PAYMENT_STATUS_CODES.SUCCESS, label: QR_STATUS_TEXT.SUCCESS },
      { value: QR_PAYMENT_STATUS_CODES.FAILED, label: QR_STATUS_TEXT.FAILED },
    ],
    type: "dropdown",
  },

  {
    source: "is_converted",
    placeholder: QR_FILTER_LABELS.CONVERSION_STATUS,
    options: [
      { value: true, label: QR_CONVERSION_TEXT.CONVERTED },
      { value: false, label: QR_CONVERSION_TEXT.PENDING },
    ],
    type: "dropdown",
  },
  {
    source: "paid_at__start_range",
    placeholder: QR_FILTER_LABELS.TRANSACTION_DATE_FROM,
    type: "date",
  },
  {
    source: "paid_at__end_range",
    placeholder: QR_FILTER_LABELS.TRANSACTION_DATE_TO,
    type: "date",
  },
];

export const tableChipsFormatter = (filterOrFactory, valueArg) => {
  const formatter = (filter, value) => {
    const numValue = Number(value);
    switch (filter) {
      case QR_FILTER_KEYS.CUSTOMER_FULL_NAME:
        return `${QR_FILTER_LABELS.CUSTOMER_NAME}: ${value}`;
      case QR_FILTER_KEYS.LINK_TITLE:
        return `${QR_FILTER_LABELS.LINK_NAME}: ${value}`;
      case QR_FILTER_KEYS.PAYER_VPA:
        return `${QR_FILTER_LABELS.UPI_ID}: ${value}`;
      case QR_FILTER_KEYS.QR_ORDER_UUID:
        return `${QR_FILTER_LABELS.TRANSACTION_ID}: ${value}`;
      case QR_FILTER_KEYS.QR_TYPE:
        return `${QR_FILTER_LABELS.QR_TYPE}: ${
          numValue === 1
            ? QR_TYPE_TEXT.DIRECT_UPI
            : QR_TYPE_TEXT.STANDARD_GATEWAY
        }`;
      case QR_FILTER_KEYS.STATUS:
        return `${QR_FILTER_LABELS.PAYMENT_STATUS}: ${
          numValue === QR_PAYMENT_STATUS_CODES.PENDING
            ? QR_STATUS_TEXT.PENDING
            : numValue === QR_PAYMENT_STATUS_CODES.SUCCESS
            ? QR_STATUS_TEXT.SUCCESS
            : QR_STATUS_TEXT.FAILED
        }`;
      case QR_FILTER_KEYS.IS_CONVERTED:
        return `${QR_FILTER_LABELS.CONVERSION_STATUS}: ${
          value === "true" || value === true
            ? QR_CONVERSION_TEXT.CONVERTED
            : QR_CONVERSION_TEXT.PENDING
        }`;
      case QR_FILTER_KEYS.PAID_AT_START_RANGE:
        return `${QR_FILTER_LABELS.TRANSACTION_DATE_FROM}: ${moment(
          value
        ).format(QR_CHIP_DATE_FORMAT)}`;
      case QR_FILTER_KEYS.PAID_AT_END_RANGE:
        return `${QR_FILTER_LABELS.TRANSACTION_DATE_TO}: ${moment(value).format(
          QR_CHIP_DATE_FORMAT
        )}`;
      default: {
        const labelMap = {
          customer_full_name: QR_FILTER_LABELS.CUSTOMER_NAME,
          [QR_FILTER_KEYS.CUSTOMER_FULL_NAME]: QR_FILTER_LABELS.CUSTOMER_NAME,
          link_title: QR_FILTER_LABELS.LINK_NAME,
          [QR_FILTER_KEYS.LINK_TITLE]: QR_FILTER_LABELS.LINK_NAME,
          payer_vpa: QR_FILTER_LABELS.UPI_ID,
          [QR_FILTER_KEYS.PAYER_VPA]: QR_FILTER_LABELS.UPI_ID,
          qr_order_uuid: QR_FILTER_LABELS.TRANSACTION_ID,
          [QR_FILTER_KEYS.QR_ORDER_UUID]: QR_FILTER_LABELS.TRANSACTION_ID,
          [QR_FILTER_KEYS.QR_TYPE]: QR_FILTER_LABELS.QR_TYPE,
          [QR_FILTER_KEYS.STATUS]: QR_FILTER_LABELS.PAYMENT_STATUS,
          [QR_FILTER_KEYS.IS_CONVERTED]: QR_FILTER_LABELS.CONVERSION_STATUS,
          [QR_FILTER_KEYS.PAID_AT_START_RANGE]:
            QR_FILTER_LABELS.TRANSACTION_DATE_FROM,
          [QR_FILTER_KEYS.PAID_AT_END_RANGE]:
            QR_FILTER_LABELS.TRANSACTION_DATE_TO,
        };
        const humanLabel = labelMap[filter];
        return humanLabel ? `${humanLabel}: ${value}` : undefined;
      }
    }
  };

  if (typeof filterOrFactory === "string") {
    return formatter(filterOrFactory, valueArg);
  }
  return formatter;
};
